<template>
  <div>
    <v-tabs
      @change="changeTab"
      v-model="activeTab"
      background-color="transparent"
      color="black"
      slider-color="themeColor"
      light
      slider-size="3"
    >
      <v-tab><lang t="PACKAGING_CLASS-TAB-GENERAL"></lang></v-tab>
      <v-tab><lang t="PACKAGING_CLASS-TAB-SURFACES"></lang></v-tab>
      <v-tab><lang t="PACKAGING_CLASS-TAB-TOLERANCES"></lang></v-tab>
    </v-tabs>


    <v-tabs-items v-model="activeTab">
      <v-tab-item class="pa-6 pt-12">
        <div class="item_container">
          <div v-if="isLoading" class="text-center ma-12">
            <v-progress-circular
              size="48"
              width="4"
              indeterminate
              color="grey lighten-2"
            ></v-progress-circular>
          </div>
          <template v-else>
            <v-form v-model="isValid">
              <v-text-field
                :label="$t('PACKAGING_CLASS-LABEL-NAME')"
                v-model="item.name"
              ></v-text-field>

              <v-row align="center" dense class="ma-0 mb-5 pa-0">
                <v-file-input
                  accept="image/png, image/jpeg"
                  :placeholder="$t('GLOBAL-FILE_UPLOAD-CHOOSE_FILE')"
                  prepend-icon="mdi-image"
                  :label="$t('PACKAGING_CLASS-LABEL-FILE_UPLOAD')"
                  :loading="isUploading ? 'green' : false"
                  :disabled="isUploading"
                  @change="imageUpload($event)"
                  @click:clear="imageRemove"
                  :key="item"
                >
                </v-file-input>

                <v-sheet
                  :key="item"
                  color="grey lighten-3"
                  width="48px"
                  height="36px"
                  class="ml-4"
                  style="display:flex; align-items: center;"
                >
                  <v-img max-height="28px" contain :src="getImgSrc()">
                    <template v-slot:placeholder>
                      <v-row
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                      >
                        <v-progress-circular
                          size="16"
                          width="2"
                          indeterminate
                          color="grey darken-3"
                        ></v-progress-circular>
                      </v-row>
                    </template>
                  </v-img>
                </v-sheet>
              </v-row>

              <v-row>
                <v-col cols="12" sm="6">
                  <v-select
                    :label="$t('PACKAGING_CLASS-LABEL-LEVEL')"
                    v-model="item.level"
                    :items="levels"
                    item-text="name"
                    item-value="value"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    :label="$t('PACKAGING_CLASS-LABEL-STRENGTH')"
                    v-model="item.strength"
                    :items="strengthItems"
                    item-text="name"
                    item-value="value"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    :label="$t('PACKAGING_CLASS-LABEL-MULTISCANMODE')"
                    v-model="item.multiscanMode"
                    :items="multiscanModes"
                    item-text="name"
                    item-value="value"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    :label="$t('PACKAGING_CLASS-LABEL-MAX_PRESSURE')"
                    v-model="item.maxPressure"
                    :items="maxPressureItems"
                    item-text="name"
                    item-value="value"
                  ></v-select>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="6" sm="4">
                  <v-switch
                    class="pa-0 ma-0"
                    dense
                    v-model="item.hasRoundBase"
                    :label="$t('PACKAGING_CLASS-LABEL-HAS_ROUND_BASE')"
                  ></v-switch>
                </v-col>
                <v-col cols="6" sm="4">
                  <v-switch
                    class="pa-0 ma-0"
                    dense
                    v-model="item.canBeFoiled"
                    :label="$t('PACKAGING_CLASS-LABEL-CAN_BE_FOILED')"
                  ></v-switch>
                </v-col>
                <v-col cols="6" sm="4">
                  <v-switch
                    class="pa-0 ma-0"
                    dense
                    v-model="item.isTransparent"
                    :label="$t('PACKAGING_CLASS-LABEL-IS_TRANSPARENT')"
                  ></v-switch>
                </v-col>
                <v-col cols="6" sm="4">
                  <v-switch
                    class="pa-0 ma-0"
                    dense
                    v-model="item.isReflecting"
                    :label="$t('PACKAGING_CLASS-LABEL-IS_REFLECTING')"
                  ></v-switch>
                </v-col>
                <v-col cols="6" sm="4">
                  <v-switch
                    class="pa-0 ma-0"
                    dense
                    v-model="item.isSuckable"
                    :label="$t('PACKAGING_CLASS-LABEL-IS_SUCKABLE')"
                  ></v-switch>
                </v-col>
                <v-col cols="6" sm="4">
                  <v-switch
                    class="pa-0 ma-0"
                    dense
                    v-model="item.isTiltable"
                    :label="$t('PACKAGING_CLASS-LABEL-IS_TILTABLE')"
                  ></v-switch>
                </v-col>
              </v-row>

              <v-row dense>

                <v-col cols="12" sm="6">
                  <v-text-field
                    :label="$t('PACKAGING_CLASS-LABEL-FRICTION')"
                    type="number"
                    v-model.number="item.friction"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    :label="$t('PACKAGING_CLASS-LABEL-OVERHANG_PERCENTAGE')"
                    type="number"
                    v-model.number="item.overhangPercentage"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    :label="$t('PACKAGING_CLASS-LABEL-BRIDGE_PERCENTAGE')"
                    type="number"
                    v-model.number="item.bridgePercentage"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    :label="$t('PACKAGING_CLASS-LABEL-NARROWING')"
                    type="number"
                    v-model.number="item.narrowing"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  elevation="0"
                  light
                  rounded
                  color="themeColor"
                  :loading="dialog.isSaving"
                  @click="itemSave()"
                  ><lang t="GLOBAL-BTN-SAVE"></lang
                ></v-btn>
              </v-card-actions>
            </v-form>
          </template>
        </div>
      </v-tab-item>

      <v-tab-item class="pa-6 pt-12">
        <div class="item_container">
          <v-row justify="center">
            <v-expansion-panels v-model="panels" focusable multiple>
              <v-expansion-panel
                v-for="(surface, surfaceName) in surfaceAreas"
                :key="surfaceName"
              >
                <v-expansion-panel-header
                  :class="{
                    inactive_settings: surface.isOverwrittenWithDefault,
                  }"
                >
                  <span class="surface_title">
                    <v-icon>{{ surface.icon }}</v-icon>
                    <lang
                      :t="
                        `PACKAGING_CLASS-SURFACES-BTN-${surfaceName.toUpperCase()}`
                      "
                    ></lang>
                    <template v-if="surface.isOverwrittenWithDefault">
                      <span style="margin: 0 8px">-</span>
                      <lang
                        :t="
                          `PACKAGING_CLASS-SURFACES-LABEL-HINT_IS_OVERWRITTEN_WITH_DEFAULT`
                        "
                      ></lang>
                    </template>
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content class="pt-6">
                  <v-row dense>
                    <v-col cols="12" sm="4">
                      <v-select
                        v-model="surface.data.nature"
                        clearable
                        :items="natureItems"
                        item-text="name"
                        item-value="value"
                        :label="$t('PACKAGING_CLASS-SURFACES-LABEL-NATURE')"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-select
                        v-model="surface.data.type"
                        clearable
                        :items="typeItems"
                        item-text="name"
                        item-value="value"
                        :label="$t('PACKAGING_CLASS-SURFACES-LABEL-TYPE')"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-select
                        v-model="surface.data.relief"
                        clearable
                        :items="reliefItems"
                        item-text="name"
                        item-value="value"
                        :label="$t('PACKAGING_CLASS-SURFACES-LABEL-RELIEF')"
                      ></v-select>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="4">
                      <v-select
                        v-model="surface.data.material"
                        clearable
                        :items="materialItems"
                        item-text="name"
                        item-value="value"
                        :label="$t('PACKAGING_CLASS-SURFACES-LABEL-MATERIAL')"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-select
                        v-model="surface.data.material_area"
                        clearable
                        :items="materialAreaItems"
                        item-text="name"
                        item-value="value"
                        :label="
                          $t('PACKAGING_CLASS-SURFACES-LABEL-MATERIAL_AREA')
                        "
                      ></v-select>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-select
                        v-model="surface.data.material_height_bars"
                        clearable
                        :items="materialAreaItems"
                        item-text="name"
                        item-value="value"
                        :label="
                          $t(
                            'PACKAGING_CLASS-SURFACES-LABEL-MATERIAL_HEIGHT_BARS'
                          )
                        "
                      ></v-select>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <v-select
                        v-model="surface.data.material_length_bars"
                        clearable
                        :items="materialAreaItems"
                        item-text="name"
                        item-value="value"
                        :label="
                          $t(
                            'PACKAGING_CLASS-SURFACES-LABEL-MATERIAL_LENGTH_BARS'
                          )
                        "
                      ></v-select>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-select
                        v-model="surface.data.material_width_bars"
                        clearable
                        :items="materialAreaItems"
                        item-text="name"
                        item-value="value"
                        :label="
                          $t(
                            'PACKAGING_CLASS-SURFACES-LABEL-MATERIAL_WIDTH_BARS'
                          )
                        "
                      ></v-select>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="4">
                      <v-text-field
                        type="number"
                        v-model.number="surface.data.pressure_area_height_bars"
                        :label="
                          $t(
                            'PACKAGING_CLASS-SURFACES-LABEL-PRESSURE_AREA_HEIGHT_BARS'
                          )
                        "
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-text-field
                        type="number"
                        v-model.number="surface.data.pressure_area_length_bars"
                        :label="
                          $t(
                            'PACKAGING_CLASS-SURFACES-LABEL-PRESSURE_AREA_LENGTH_BARS'
                          )
                        "
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-text-field
                        type="number"
                        v-model.number="surface.data.pressure_area_width_bars"
                        :label="
                          $t(
                            'PACKAGING_CLASS-SURFACES-LABEL-PRESSURE_AREA_WIDTH_BARS'
                          )
                        "
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="4">
                      <v-text-field
                        type="number"
                        v-model.number="surface.data.dimension_height_bars"
                        :label="
                          $t(
                            'PACKAGING_CLASS-SURFACES-LABEL-DIMENSION_HEIGHT_BARS'
                          )
                        "
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-text-field
                        type="number"
                        v-model.number="surface.data.dimension_length_bars"
                        :label="
                          $t(
                            'PACKAGING_CLASS-SURFACES-LABEL-DIMENSION_LENGTH_BARS'
                          )
                        "
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-text-field
                        type="number"
                        v-model.number="surface.data.dimension_width_bars"
                        :label="
                          $t(
                            'PACKAGING_CLASS-SURFACES-LABEL-DIMENSION_WIDTH_BARS'
                          )
                        "
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-row>
          <v-row>
            <v-col class="d-flex justify-end pr-0">
              <v-btn
                elevation="0"
                light
                rounded
                color="themeColor"
                :loading="dialog.isSaving"
                @click="itemSave()"
              >
                <lang t="GLOBAL-BTN-SAVE"></lang>
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-tab-item>

      <v-tab-item class="pa-6 pt-12">
        <div class="item_container">
          <div v-if="isLoading" class="text-center ma-12">
            <v-progress-circular
              size="48"
              width="4"
              indeterminate
              color="grey lighten-2"
            ></v-progress-circular>
          </div>
          <template v-else>

            <v-form v-model="isValid">

              <div class="subtitle-1 mb-2"><lang :t="`PACKAGING_CLASS-LABEL-TOLERANCES-WIDTH`"></lang></div>

              <v-row dense class="mb-2">
                <v-col cols="6" sm="6">
                  <v-text-field
                    :label="$t('PACKAGING_CLASS-LABEL-TOLERANCES-WIDTH_PLUS')"
                    type="number"
                    v-model.number="item.tolerances.width_plus"
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="6">
                  <v-text-field
                    :label="$t('PACKAGING_CLASS-LABEL-TOLERANCES-WIDTH_MINUS')"
                    type="number"
                    v-model.number="item.tolerances.width_minus"
                  ></v-text-field>
                </v-col>
              </v-row>

              <div class="subtitle-1 my-2"><lang :t="`PACKAGING_CLASS-LABEL-TOLERANCES-LENGTH`"></lang></div>

              <v-row dense class="mb-2">
                <v-col cols="6" sm="6">
                  <v-text-field
                    :label="$t('PACKAGING_CLASS-LABEL-TOLERANCES-LENGTH_PLUS')"
                    type="number"
                    v-model.number="item.tolerances.length_plus"
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="6">
                  <v-text-field
                    :label="$t('PACKAGING_CLASS-LABEL-TOLERANCES-LENGTH_MINUS')"
                    type="number"
                    v-model.number="item.tolerances.length_minus"
                  ></v-text-field>
                </v-col>
              </v-row>

              <div class="subtitle-1 my-2"><lang :t="`PACKAGING_CLASS-LABEL-TOLERANCES-HEIGHT`"></lang></div>

              <v-row dense class="mb-2">
                <v-col cols="6" sm="6">
                  <v-text-field
                    :label="$t('PACKAGING_CLASS-LABEL-TOLERANCES-HEIGHT_PLUS')"
                    type="number"
                    v-model.number="item.tolerances.height_plus"
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="6">
                  <v-text-field
                    :label="$t('PACKAGING_CLASS-LABEL-TOLERANCES-HEIGHT_MINUS')"
                    type="number"
                    v-model.number="item.tolerances.height_minus"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  elevation="0"
                  light
                  rounded
                  color="themeColor"
                  :loading="dialog.isSaving"
                  @click="itemSave()"
                  ><lang t="GLOBAL-BTN-SAVE"></lang
                ></v-btn>
              </v-card-actions>
            </v-form>
          </template>
        </div>

      </v-tab-item>

    </v-tabs-items>
  </div>
</template>

<script>
import Vue from "vue";

export default Vue.extend({
  data() {
    return {
      isSaving: false,
      isLoading: true,
      isUploading: false,
      activeTab: 0,
      item: {
        name: null,
        level: null,
        image: null,
        imageTmp: null,
        multiscanMode: null,
        hasRoundBase: false,
        canBeFoiled: false,
        tolerances: {

        },
        surfaces: {
        },
      },
      dialog: {
        show: false,
        title: "",
        isSaving: false,
      },
      surfaceAreas: {
        default: {
          icon: "mdi-checkbox-multiple-blank-outline",
          data: {},
        },
        top: { icon: "mdi-arrow-up-bold-box-outline", data: {} },
        bottom: {
          icon: "mdi-arrow-down-bold-box-outline",
          data: {},
        },
        left: {
          icon: "mdi-arrow-left-bold-box-outline",
          data: {},
        },
        right: {
          icon: "mdi-arrow-right-bold-box-outline",
          data: {},
        },
        front: { icon: "mdi-circle-box-outline", data: {} },
        back: { icon: "mdi-checkbox-blank-outline", data: {} },
      },
      dataIsSet: false,
      panels: [],
      defaultObjScaffold: {
        nature: null,
        type: null,
        relief: null,
        material: null,
        material_area: null,
        material_height_bars: null,
        material_length_bars: null,
        material_width_bars: null,
        pressure_area_height_bars: null,
        pressure_area_length_bars: null,
        pressure_area_width_bars: null,
        dimension_height_bars: null,
        dimension_length_bars: null,
        dimension_width_bars: null,
      },
    };
  },

  created() {
    this.initialize();
  },

  watch: {
    "surfaceAreas.default": {
      handler: function(newDefaultObj) {
        for (const surfaceName in this.surfaceAreas) {
          if (this.surfaceAreas[surfaceName].isOverwrittenWithDefault) {
            this.surfaceAreas[surfaceName].data = { ...newDefaultObj.data };
          }
        }
      },
      deep: true,
    },
    surfaceAreas: {
      handler: function(newSurfaceAreas) {
        const defaultObj = newSurfaceAreas.default;
        const topObj = newSurfaceAreas.top;
        const bottomObj = newSurfaceAreas.bottom;
        const leftObj = newSurfaceAreas.left;
        const rightObj = newSurfaceAreas.right;
        const frontObj = newSurfaceAreas.front;
        const backObj = newSurfaceAreas.back;
        this.setOverrideStatus({ surfaceObj: topObj, defaultObj });
        this.setOverrideStatus({ surfaceObj: bottomObj, defaultObj });
        this.setOverrideStatus({ surfaceObj: leftObj, defaultObj });
        this.setOverrideStatus({ surfaceObj: rightObj, defaultObj });
        this.setOverrideStatus({ surfaceObj: frontObj, defaultObj });
        this.setOverrideStatus({ surfaceObj: backObj, defaultObj });
      },
      deep: true,
    },
    $route: function(newVal,oldVal) {
        if (oldVal.name === "settings-packaging_classes-detail-new" ) {
            this.initialize();
        }
    },
  },
  computed: {
    levels() {
      return [
        {
          name: this.$t("PACKAGING_CLASS-LEVEL-CASE"),
          value: "case",
        },
        {
          name: this.$t("PACKAGING_CLASS-LEVEL-PACK"),
          value: "pack",
        },
        {
          name: this.$t("PACKAGING_CLASS-LEVEL-UNIT"),
          value: "unit",
        },
      ];
    },

    multiscanModes() {
      return [
        {
          name: this.$t("PACKAGING_CLASS-MULTISCANMODE-COMPLEX"),
          value: "complex",
        },
        {
          name: this.$t("PACKAGING_CLASS-MULTISCANMODE-CUBIC"),
          value: "cubic",
        },
        {
          name: this.$t("PACKAGING_CLASS-MULTISCANMODE-TEXPRES"),
          value: "texpres",
        },
      ];
    },
    materialItems() {
      return [
        {
          name: this.$t("PACKAGING_CLASS-MATERIAL-CARTON"),
          value: "carton",
        },
        {
          name: this.$t("PACKAGING_CLASS-MATERIAL-CARTON_ROUGH"),
          value: "carton_rough",
        },
        {
          name: this.$t("PACKAGING_CLASS-MATERIAL-PLASTIC"),
          value: "plastic",
        },
      ];
    },
    materialAreaItems() {
      return [
        {
          name: this.$t("PACKAGING_CLASS-MATERIAL-CARTON"),
          value: "carton",
        },
        {
          name: this.$t("PACKAGING_CLASS-MATERIAL-PLASTIC"),
          value: "plastic",
        },
      ];
    },
    natureItems() {
      return [
        {
          name: this.$t("PACKAGING_CLASS-NATURE-INHERIT"),
          value: "inherit",
        },
        {
          name: this.$t("PACKAGING_CLASS-NATURE-EVEN"),
          value: "even",
        },
        {
          name: this.$t("PACKAGING_CLASS-NATURE-UNEVEN"),
          value: "uneven",
        },
      ];
    },
    reliefItems() {
      return [
        {
          name: this.$t("PACKAGING_CLASS-RELIEF-INHERIT"),
          value: "inherit",
        },
        {
          name: this.$t("PACKAGING_CLASS-RELIEF-YES"),
          value: "yes",
        },
        {
          name: this.$t("PACKAGING_CLASS-RELIEF-NO"),
          value: "no",
        },
      ];
    },
    strengthItems() {
      return [
        {
          name: this.$t("PACKAGING_CLASS-STRENGTH-SOFT"),
          value: "SOFT",
        },
        {
          name: this.$t("PACKAGING_CLASS-STRENGTH-HARD"),
          value: "HARD",
        },
      ];
    },
    maxPressureItems() {
      return [
        {
          name: this.$t("PACKAGING_CLASS-MAX_PRESSUE-LOW"),
          value: 1500,
        },
        {
          name: this.$t("PACKAGING_CLASS-MAX_PRESSUE-MEDIUM"),
          value: 4500,
        },
        {
          name: this.$t("PACKAGING_CLASS-MAX_PRESSUE-HIGH"),
          value: 11000,
        },
      ];
    },
    typeItems() {
      return [
        {
          name: this.$t("PACKAGING_CLASS-TYPE-FULL"),
          value: "FULL",
        },
        {
          name: this.$t("PACKAGING_CLASS-TYPE-PERIMETER"),
          value: "PERIMETER",
        },
        {
          name: this.$t("PACKAGING_CLASS-TYPE-TAPERED"),
          value: "TAPERED",
        },
        {
          name: this.$t("PACKAGING_CLASS-TYPE-LENGTH_BARS"),
          value: "LENGTH_BARS",
        },
        {
          name: this.$t("PACKAGING_CLASS-TYPE-WIDTH_BARS"),
          value: "WIDTH_BARS",
        },
        {
          name: this.$t("PACKAGING_CLASS-TYPE-HEIGHT_BARS"),
          value: "HEIGHT_BARS",
        },
        {
          name: this.$t("PACKAGING_CLASS-TYPE-CORNERS"),
          value: "CORNERS",
        },
        {
          name: this.$t("PACKAGING_CLASS-TYPE-WIDTH_DISPLAY"),
          value: "WIDTH_DISPLAY",
        },
        {
          name: this.$t("PACKAGING_CLASS-TYPE-HEIGHT_DISPLAY"),
          value: "HEIGHT_DISPLAY",
        },
      ];
    },
  },
  methods: {
    initialize() {
      if (!this.$route.params?.id) {
        this.item.surfaces.default = this.defaultObjScaffold;
        this.setSurfaceData(this.item.surfaces);
        this.isLoading = false;
      }
      else {
        this.axios.get(`bundletypes/${this.$route.params.id}`).then((res) => {
          this.item = res.data;
          this.isLoading = false;
          if (!this.item.surfaces) {
            this.item.surfaces = { default: this.defaultObjScaffold };
          }
          this.setSurfaceData(this.item.surfaces);
          this.changeTitle(this.item);
        });
      }
    },
    changeTitle(itemObj) {
      this.$route.meta.pageTitle =
        this.$t("SETTINGS-PACKAGING_CLASSES-DETAIL-HEADING") +
        " " +
        this.$t("bundletypes_" + itemObj.level + "_" + itemObj.name);
      // Add a temporary query parameter.
      this.$router.replace({ query: { temp: Date.now() } });

      // Remove the temporary query parameter.
      this.$router.replace({ query: { temp: undefined } });
    },
    setSurfaceData(NewSurfaceObjects) {
      const surfacesFromAPI = { ...NewSurfaceObjects };
      for (const [surfaceName, surfaceObj] of Object.entries(
        this.surfaceAreas
      )) {
        if (surfacesFromAPI[surfaceName]) {
          surfaceObj.data = surfacesFromAPI[surfaceName];
        } else {
          surfaceObj.data = { ...surfacesFromAPI["default"] };
          surfaceObj.isOverwrittenWithDefault = true;
        }
      }
    },
    setOverrideStatus({ surfaceObj, defaultObj }) {
      surfaceObj.isOverwrittenWithDefault = true;
      for (const key in surfaceObj.data) {
        if (surfaceObj.data[key] !== defaultObj.data[key]) {
          surfaceObj.isOverwrittenWithDefault = false;
          break;
        }
      }
    },
    getImgSrc() {
      if (this.isUploading) {
        return false;
      }

      let imgUrl = false;

      if (this.item.imageTmp != null) {
        imgUrl = this.item.imageTmp;
      } else {
        imgUrl =
          this.item.image != null ? this.$config.cdn + this.item.image : false;
      }

      return imgUrl;
    },
    imageRemove() {
      this.item.image = null;
      this.item.imageTmp = null;
      this.$forceUpdate();
    },

    imageUpload(file) {
      if (typeof file == "undefined") {
        return;
      }

      this.isUploading = true;

      let formData = new FormData();
      formData.append("file", file, file.name);

      // additional data
      formData.append("key", "file");

      this.axios
        .post("/upload", formData)
        .then((response) => {
          this.item.image = response.data.filename;
          this.item.imageTmp = response.data.url;

          setTimeout(() => {
            this.isUploading = false;
          }, 1000);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    itemEdit(item) {
      this.dialog.title = this.$t("PACKAGING_CLASSES-EDIT_LHF");
      this.editedIndex = this.items.indexOf(item);
      this.item = Object.assign({}, item);

      this.dialog.show = true;
    },

    itemAdd() {
      this.dialog.title = this.$t("PACKAGING_CLASSES-NEW_PACKAGING_CLASS");
      this.dialog.show = true;
    },

    itemSave() {
      // https://knapp.jumptoday.group/bl/public/api/upload

      this.dialog.isSaving = true;
      if (this.item.surfaces) {
        for (const surfaceName in this.surfaceAreas) {
          if (!this.surfaceAreas[surfaceName].isOverwrittenWithDefault) {
            this.item.surfaces[surfaceName] = this.surfaceAreas[
              surfaceName
            ].data;
          }
        }
      }
      if (!this.item.id) {
        this.dialog.isSaving = false;
        this.axios
          .post("bundletypes", this.item)
          .then((res) => {
            this._fin();
            this.item.id = res.data.id;
            this.$router.push({
              name: "settings-packaging_classes-detail",
              params: { id: res.data.id }
            });
          })
          .catch((err) => {
            this.dialog.isSaving = false;
            this.$store.dispatch("setSnackbar", {
              text:
                this.$t("GLOBAL-MESSAGE-ERROR") +
                ": " +
                err.response.data.message,
              type: "error",
            });
          });
      }
      else {
        this.axios
          .put("bundletypes/" + this.item.id, this.item)
          .then((res) => {
            this._fin();
          })
          .catch((err) => {
            this.dialog.isSaving = false;
            this.$store.dispatch("setSnackbar", {
              text:
                this.$t("GLOBAL-MESSAGE-ERROR") +
                ": " +
                err.response.data.message,
              type: "error",
            });
          });
      }
    },

    _fin() {
      this.dialog.isSaving = false;
      this.$store.dispatch("setSnackbar", {
        text: this.$t("GLOBAL-MESSAGE-SUCCESSFULLY_SAVED"),
      });
    },
  },
});
</script>

<style lang="scss" scoped>
.item_container {
  max-width: calc(960px - 220px);
  @media screen and (max-width: 600px) {
    padding: unset;
  }
}
.surface_title {
  margin-top: 3px;
  display: flex;
  align-items: center;
  i {
    margin-right: 8px;
  }
}
.inactive_settings {
  opacity: 0.5;
}
</style>
